import { Typography, Zoom, Backdrop, useTheme } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { useCamerasForPatient } from '../../api/index.js';
import { useCheckPermissions } from '../../auth/index.js';
import { ActivityDebugWidget } from '../../components/Activity/index.js';
import { Loading } from '../../components/index.js';
import { LayoutGrid } from '../../components/Layout/index.js';
import PatientStreamContainer from '../../components/Patients/PatientStreamContainer.js';
import { useInactivityCheck } from '../../hooks/index.js';
import { type Camera } from '../../models/index.js';
import { isLayoutEditing, isVideoPlayingSelector } from '../../redux/selectors.js';
import { Events, track, useEventSource } from '../../utils/index.js';

type PatientParams = {
  patientId: string;
};

const PatientProfile = () => {
  const [highlightedCameraId, setHighlightedCameraId] = useState<string | null>(null);
  const [fullscreen, setFullScreen] = useState<Camera>();
  const [open, setOpen] = React.useState<boolean>(false);

  const { patientId = '' } = useParams<PatientParams>();
  const isVideoPlaying = useSelector(isVideoPlayingSelector(patientId));
  const layoutScope = `patient:${patientId}`;
  const editMode = useSelector(useMemo(() => isLayoutEditing(layoutScope), [layoutScope]));
  const theme = useTheme();
  const inactivity = useInactivityCheck();

  const eventSource = useEventSource(`${import.meta.env.VITE_API_BASE_URL}/streams/sse/${patientId}`, {
    shouldListen: true,
  });

  useEffect(() => {
    const onMessage = (message: MessageEvent) => {
      const { cameraId, motion } = JSON.parse(message.data);
      if (motion) {
        // Motion no longer reflows the layout — operators own their saved layout.
        // Pulse the affected tile for 20s so they can spot it without losing
        // their arrangement.
        setHighlightedCameraId(cameraId);
        setTimeout(() => {
          setHighlightedCameraId((curr) => (curr === cameraId ? null : curr));
        }, 20 * 1000);
      }
    };

    // message = when an event is received from the server
    eventSource?.addEventListener('message', onMessage);
    // error = when an SSE-related error is received from the server
    eventSource?.addEventListener('error', function (err) {
      console.error('error', err);
    });
    // clean up
    return () => {
      eventSource?.removeEventListener('message', onMessage);
      eventSource?.removeEventListener('error', function (err) {
        console.error('error', err);
      });
    };
  }, [eventSource?.readyState]);

  const cameras = useCamerasForPatient(patientId);

  const { canReadFeed, canReadActivityEvent } = useCheckPermissions();

  useEffect(() => {
    track(Events.PatientProfileView, {
      patientId,
    });
  }, []);

  if (!canReadFeed) {
    return <div>Not allowed to view streams</div>;
  }
  if (cameras.isLoading) {
    return <Loading />;
  }

  const onTileClick = (cameraId: string) => {
    const match = cameras?.data?.find((camera) => camera.id === cameraId);
    setFullScreen(match);
    setOpen(true);
  };
  const handleFullscreenClose = () => {
    setOpen(false);
    setTimeout(() => {
      setFullScreen(undefined);
    }, theme.transitions.duration.leavingScreen);
  };

  const layoutItems = (cameras.data ?? []).map((c) => ({ id: c.id }));

  return (
    <>
      {cameras.data && cameras.data.length > 0 ? (
        <LayoutGrid scope={layoutScope} editMode={editMode} items={layoutItems}>
          {cameras.data.map((camera) => (
            <div
              key={camera.id}
              data-motion-active={highlightedCameraId === camera.id ? 'true' : undefined}
            >
              <PatientStreamContainer
                cameraId={camera.id}
                cameraName={camera.name}
                isPlaying={isVideoPlaying as boolean}
                inactivity={inactivity}
                handleFocusStream={onTileClick}
                lastActivity={camera.lastActivity}
                layoutScope={layoutScope}
              />
            </div>
          ))}
        </LayoutGrid>
      ) : (
        <Typography variant={'h6'}>No Cameras Available!</Typography>
      )}
      <Backdrop open={open} onClick={handleFullscreenClose} sx={(theme) => ({ zIndex: theme.zIndex.drawer + 1 })}>
        <Zoom
          in={open}
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90vw',
            zIndex: 10000,
          }}
        >
          <PatientStreamContainer
            cameraId={fullscreen?.id as string}
            cameraName={fullscreen?.name as string}
            isPlaying={isVideoPlaying as boolean}
            inactivity={inactivity}
            handleFocusStream={handleFullscreenClose}
          />
        </Zoom>
      </Backdrop>
      {canReadActivityEvent ? <ActivityDebugWidget /> : null}
    </>
  );
};

export default PatientProfile;
